import React, { useState, useEffect } from "react";
import API from "../api";
import SEO from "../components/SEO";

import Hero from "../components/Hero";
import About from "../components/About";
import Counts from "../components/Counts";
import WhyUs from "../components/WhyUs";

// Fallback slides in case API fails
const fallbackSlides = [
    {
        id: 1,
        image: "/assets/img/hero-1.jpg",
        title: "Africa Research University",
        subtitle: "Dedicated to nurturing exceptional individuals. We don't just educate; we inspire transformation",
        buttonText: "Apply Now",
        buttonLink: "/apply"
    },
    {
        id: 2,
        image: "/assets/img/hero-2.jpg",
        title: "Excellence in Education",
        subtitle: "Pioneering research and academic innovation across Africa",
        buttonText: "Explore Programs",
        buttonLink: "/courses"
    },
    {
        id: 3,
        image: "/assets/img/hero-3.jpg",
        title: "Transform Your Future",
        subtitle: "Join a community of scholars, innovators, and leaders shaping tomorrow",
        buttonText: "Apply Now",
        buttonLink: "/apply"
    }
];

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const HomePage = () => {
    const [slides, setSlides] = useState(fallbackSlides);
    const [loading, setLoading] = useState(true);

    // const apiUrl = 'http://localhost:4000';

    // Ensure the page scrolls to top whenever this page is navigated to or the id changes
    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && window?.scrollTo) {
                // instant jump to top to avoid flashing at previous scroll position
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
        } catch (e) {
            // fallback: simple assignment
            try {
                window.scrollTo(0, 0);
            } catch (err) {
                // ignore in environments without window
            }
        }
    }, []);

    useEffect(() => {
        let mounted = true;

        const fetchSlides = async () => {
            setLoading(true);
            try {
                const res = await API.get('/hero-slides');
                const data = res.data;

                if (mounted && Array.isArray(data) && data.length > 0) {
                    // Transform API data to match Hero component props
                    const transformedSlides = data.map(slide => ({
                        id: slide.id,
                        image: slide.image?.startsWith('http') ? slide.image : `${apiUrl}${slide.image}`,
                        title: slide.title,
                        subtitle: slide.subtitle || "",
                        buttonText: slide.button_text || "Learn More",
                        buttonLink: slide.button_link || "#",
                        showButton: slide.show_button === undefined || slide.show_button === null ? true : Boolean(slide.show_button)
                    }));

                    setSlides(transformedSlides);
                }
            } catch (err) {
                console.error('Failed to load hero slides, using fallback:', err);
                // Keep fallback slides on error
            } finally {
                if (mounted) setLoading(false);
            }
        };

        fetchSlides();

        return () => {
            mounted = false;
        };
    }, []);

    if (loading) {
        return (
            <div className="pt-12">
                <div className="text-center py-20 text-gray-600">Loading...</div>
            </div>
        );
    }

    const seoJsonLd = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "url": "https://africaresearchuniversity.net",
        "name": "Africa Research University",
        "description": "ARU - Excellence in research and higher education across Africa",
    };

    return (
        <div className="pt-1">
            <SEO title="Africa Research University – Home" description="Explore programs, apply, and learn about ARU" jsonLd={seoJsonLd} />
            <Hero
                slides={slides}
                interval={6000}
                height="420px"
                borderRadius="50px"
                margin="60px"
            />
            <Counts />
            <About />
            <WhyUs />
        </div>
    );
};

export default HomePage;